import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Divider, Select } from '@mantine/core';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import ViewProductPrintImagesCanvasGrid from '../products/ViewProductPrintImagesCanvasGrid';
import ViewProductPrintImagesCanvasControls from '../products/ViewProductPrintImagesCanvasControls';
import AppFlexbox from '../../common/AppFlexbox';
import { ECOM_IMAGE_POSITION_LIST } from '../../../config/constants';

const ViewOrderProductPrintImagesModal = ({ ecomOrder, isOpen, onClose }) => {
  const containerRef = useRef(null);
  const [viewState, setViewState] = useState({
    selectedPrintDesignId: null,
    selectedPrintImageValue: null,
    showGridlines: false,
    showOriginalCanvasSize: false,
    showBackgroundFill: false
  });

  const productOptions =
    ecomOrder?.ecomOrderProducts
      .filter((p) => p.printImages && p.printImages.length > 0)
      .map((p) => ({
        label:
          p.ecomVendorProductVariant?.ecomVendorProduct.name ||
          p.ecomStoreProductVariant?.ecomStoreProduct.ecomVendorProduct.name ||
          '',
        value: p.pkEcomOrderProduct.toString(),
        printImages: p.printImages.map((pi) => ({
          pkEcomVendorProductPrintDesignImage: pi.pkEcomVendorProductPrintDesignImage.toString(),
          label:
            ECOM_IMAGE_POSITION_LIST.find(
              (f) => f.value === pi.fkEcomImagePosition.toString()
            )?.label || '',
          value: pi.pkEcomVendorProductPrintDesignImage.toString(),
          src: pi.src,
          imageWidthPx: pi.imageWidthPx,
          imageHeightPx: pi.imageHeightPx
        }))
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const selectProduct =
    productOptions.find((p) => p.value === viewState.selectedPrintImageValue) ||
    null;

  const selectedPrintImage =
    selectProduct?.printImages.find(
      (i) =>
        i.pkEcomVendorProductPrintDesignImage ===
        viewState.selectedPrintDesignId
    ) || null;

  useEffect(() => {
    if (isOpen) {
      setViewState((prevState) => ({
        ...prevState,
        selectedPrintDesignId:
          productOptions[0]?.printImages[0]
            ?.pkEcomVendorProductPrintDesignImage || null,
        selectedPrintImageValue: productOptions[0]?.value || null,
        showGridlines: false,
        showOriginalCanvasSize: false,
        showBackgroundFill: false
      }));
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectProduct) {
      const defaultImage = selectProduct.printImages[0];
      setViewState((prevState) => ({
        ...prevState,
        selectedPrintDesignId: defaultImage.pkEcomVendorProductPrintDesignImage,
        selectedPrintImageValue: selectProduct.value
      }));
    }
  }, [viewState.selectedPrintImageValue]);

  const onDownloadImage = (image) => {
    fetch(image.src)
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        // Set filename
        a.download = image.src.split('/').pop();
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => console.error('Failed to download', image.src, err));
  };

  const downloadImages = async () => {
    productOptions.forEach((product) => {
      product.printImages.forEach(onDownloadImage);
    });
  };

  return (
    ecomOrder && (
      <ResponsiveModal
        formSectionProps={{
          onClose,
          cancelTitle: 'Close',
          cancelVariant: 'filled',
          isSubmitHidden: true,
          leftSection: (
            <Anchor
              onClick={downloadImages}
              style={{ fontSize: 14, color: 'dodgerblue' }}
            >
              Download all images
            </Anchor>
          )
        }}
        isOpen={isOpen}
        onClose={onClose}
        size={700}
        title="Order print images"
      >
        <AppStack style={{ gap: 20 }}>
          <AppText
            style={{
              fontSize: 14,
              fontStyle: 'italic',
              color: '#666',
              textAlign: 'center'
            }}
          >
            These images were submitted to the print provider.
          </AppText>

          <Divider />

          <AppStack style={{ gap: 5 }}>
            {selectProduct?.printImages?.length > 1 && (
              <AppFlexbox>
                <Select
                  data={selectProduct.printImages.map((d) => ({
                    value: d.pkEcomVendorProductPrintDesignImage.toString(),
                    label: d.label
                  }))}
                  onChange={(value) =>
                    setViewState((prev) => ({
                      ...prev,
                      selectedPrintDesignId: value
                    }))
                  }
                  placeholder="Position"
                  value={viewState.selectedPrintDesignId}
                />
              </AppFlexbox>
            )}

            <ViewProductPrintImagesCanvasControls
              formState={viewState}
              onDownloadImage={
                selectedPrintImage
                  ? () => {
                      if (selectedPrintImage) {
                        onDownloadImage(selectedPrintImage);
                      }
                    }
                  : null
              }
              options={productOptions}
              setFormState={setViewState}
            />

            <AppStack
              ref={containerRef}
              style={{
                gap: 5,
                border: 'solid 1px #363434ff'
              }}
            >
              {!selectedPrintImage ? (
                <AppText
                  style={{
                    padding: 20,
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}
                >
                  No product selected.
                </AppText>
              ) : (
                <ViewProductPrintImagesCanvasGrid
                  originalCanvasHeight={selectedPrintImage?.imageHeightPx}
                  originalCanvasWidth={selectedPrintImage?.imageWidthPx}
                  showBackgroundFill={viewState.showBackgroundFill}
                  showGrid={viewState.showGridlines}
                  showOriginalCanvasSize={viewState.showOriginalCanvasSize}
                  src={selectedPrintImage?.src}
                />
              )}
            </AppStack>
          </AppStack>
        </AppStack>
      </ResponsiveModal>
    )
  );
};

ViewOrderProductPrintImagesModal.propTypes = {
  ecomOrder: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

export default ViewOrderProductPrintImagesModal;
